import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { sendOtpEmail } from './emailService';

export interface CustomerProfile {
  id?: number;
  email: string;
  full_name?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  preferences?: Record<string, any>;
  access_token?: string;
  token_expires_at?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Crée ou met à jour un profil client
 */
export const createOrUpdateCustomerProfile = async (profileData: CustomerProfile): Promise<CustomerProfile | null> => {
  try {
    // Vérifier si le profil existe déjà
    const { data: existingProfile } = await supabase
      .from('shop_customer_profiles')
      .select('*')
      .eq('email', profileData.email)
      .single();

    if (existingProfile) {
      // Mettre à jour le profil existant
      const { data, error } = await supabase
        .from('shop_customer_profiles')
        .update({
          full_name: profileData.full_name || existingProfile.full_name,
          phone: profileData.phone || existingProfile.phone,
          address: profileData.address || existingProfile.address,
          city: profileData.city || existingProfile.city,
          country: profileData.country || existingProfile.country,
          postal_code: profileData.postal_code || existingProfile.postal_code,
          preferences: { ...existingProfile.preferences, ...profileData.preferences }
        })
        .eq('email', profileData.email)
        .select()
        .single();

      if (error) throw error;
      return data;
    } else {
      // Créer un nouveau profil
      const { data, error } = await supabase
        .from('shop_customer_profiles')
        .insert([profileData])
        .select()
        .single();

      if (error) throw error;
      return data;
    }
  } catch (error) {
    console.error('Erreur lors de la création/mise à jour du profil client:', error);
    return null;
  }
};

/**
 * Récupère un profil client par email
 */
export const getCustomerProfileByEmail = async (email: string): Promise<CustomerProfile | null> => {
  try {
    const { data, error } = await supabase
      .from('shop_customer_profiles')
      .select('*')
      .eq('email', email)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération du profil client:', error);
    return null;
  }
};

/**
 * Récupère un profil client par token d'accès
 */
export const getCustomerProfileByToken = async (token: string): Promise<CustomerProfile | null> => {
  try {
    const { data, error } = await supabase
      .from('shop_customer_profiles')
      .select('*')
      .eq('access_token', token)
      .gt('token_expires_at', new Date().toISOString())
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération du profil client par token:', error);
    return null;
  }
};

/**
 * Génère un code OTP à 6 chiffres
 */
export const generateOTP = (): string => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

/**
 * Envoie un code OTP par email
 */
export const sendCustomerOTP = async (email: string): Promise<string | null> => {
  try {
    const otp = generateOTP();
    
    // Stocker l'OTP dans localStorage (en production, utiliser une méthode plus sécurisée)
    localStorage.setItem(`customer_otp_${email}`, otp);
    localStorage.setItem(`customer_otp_expiry_${email}`, (Date.now() + 10 * 60 * 1000).toString()); // 10 minutes d'expiration
    
    // Envoyer l'email avec l'OTP
    await sendOtpEmail({
      to: email,
      name: email,
      otpCode: otp
    });
    
    return otp;
  } catch (error) {
    console.error('Erreur lors de l\'envoi du code OTP:', error);
    return null;
  }
};

/**
 * Vérifie un code OTP
 */
export const verifyCustomerOTP = (email: string, otp: string): boolean => {
  const storedOTP = localStorage.getItem(`customer_otp_${email}`);
  const expiryTime = localStorage.getItem(`customer_otp_expiry_${email}`);
  
  // Vérifier si l'OTP est expiré
  if (!storedOTP || !expiryTime || parseInt(expiryTime) < Date.now()) {
    return false;
  }
  
  // Vérifier si l'OTP est correct
  return storedOTP === otp;
};

/**
 * Génère un token d'accès pour un client
 */
export const generateAccessToken = async (email: string): Promise<string | null> => {
  try {
    const token = uuidv4();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 30); // Token valide 30 jours
    
    const { error } = await supabase
      .from('shop_customer_profiles')
      .update({
        access_token: token,
        token_expires_at: expiresAt.toISOString()
      })
      .eq('email', email);

    if (error) throw error;
    return token;
  } catch (error) {
    console.error('Erreur lors de la génération du token d\'accès:', error);
    return null;
  }
};

/**
 * Invalide un token d'accès (déconnexion)
 */
export const invalidateAccessToken = async (token: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('shop_customer_profiles')
      .update({
        access_token: null,
        token_expires_at: null
      })
      .eq('access_token', token);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Erreur lors de l\'invalidation du token d\'accès:', error);
    return false;
  }
};
